import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react'

/**
 * Theme system for iuvo Guardian (Phase 2 of brand rebrand).
 *
 * The CSS variable foundation lives in `index.css`:
 *   - `:root`  defines the dark-mode token palette (default).
 *   - `.light` (applied to `<html>`) overrides those tokens with light-mode values.
 *
 * This provider owns the React-side state that controls which class is on
 * `<html>` and persists the user's choice across sessions via localStorage.
 */

export type Theme = 'dark' | 'light'

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const STORAGE_KEY = 'guardian-theme'
const DEFAULT_THEME: Theme = 'dark'

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

/**
 * Read the stored theme synchronously. Wrapped in try/catch because
 * localStorage access can throw in private-browsing modes, sandboxed
 * iframes, or any non-browser environment (SSR, tests).
 */
function readStoredTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'dark' || stored === 'light') {
      return stored
    }
  } catch {
    // localStorage unavailable — fall through to default.
  }
  return DEFAULT_THEME
}

function applyThemeClass(theme: Theme): void {
  // Single source of truth for translating Theme -> DOM class state.
  document.documentElement.classList.toggle('light', theme === 'light')
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Synchronous initializer: the very first render already knows the user's
  // preferred theme, so React's first commit reflects it. localStorage read
  // is fast and intentional here (not deferred) to avoid a flash of wrong theme.
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)

  // useLayoutEffect (not useEffect) so the `.light` class is applied to
  // `<html>` BEFORE the browser paints. This is what actually prevents the
  // flash of wrong theme: the previous session may have left `.light` on
  // `<html>` while the new state is `'dark'` (or vice versa), and a regular
  // useEffect would let the wrong palette paint for one frame.
  useLayoutEffect(() => {
    applyThemeClass(theme)
  }, [theme])

  // Persist after paint — no need to block the commit on a localStorage write.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Best-effort persistence; ignore quota / private-mode failures.
    }
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'))
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * Access the current theme and theme controls.
 * Throws if used outside <ThemeProvider> — surfaces the bug at dev time
 * instead of silently returning a default.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (ctx === undefined) {
    throw new Error('useTheme must be used within a <ThemeProvider>')
  }
  return ctx
}
